import { observer } from "mobx-react-lite";
import React from "react";
import Button from "@edaz/edazkit/Button/index";
import { formatDate, formatDateTime, translateEnum, translateGeneral } from "utils/localizations";
import { busyIdentificator } from "utils/busyWatcher";
import { IconType } from "@edaz/edazkit/Icon";
import { preventDefault, registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import { Col, Form, Row } from "react-bootstrap";
import FormLabel from "controls/formLabel";
import StaticControlReportsListViewModel from "viewModels/enforcement/reporting/staticControlReports/staticControlReportsListViewModel";
import DatePickerBox from "controls/datePickerBox";
import { Input } from "@frui.ts/bootstrap";
import { isDirty } from "@frui.ts/dirtycheck";
import type InternalReportResponse from "entities/internalReportResponse";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import ReportRequestStatus from "entities/reportRequestStatus";
import ListView from "views/common/list";

const StaticControlReportsView: ViewComponent<StaticControlReportsListViewModel> = observer(({ vm }) => {
  const isDownloadBusy = vm.busyWatcher.checkBusy(
    busyIdentificator([vm.request.reportDateRange.from, vm.request.reportDateRange.to, vm.request.gatewayId])
  );

  const columns: ColumnDefinition<InternalReportResponse>[] = [
    {
      title: vm.translateAttribute("fileName"),
      property: "fileName",
    },
    {
      title: vm.translateAttribute("fromDate"),
      valueFormatter: props => formatDate(props.value),
      property: "fromDate",
    },
    {
      title: vm.translateAttribute("processedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "processedAt",
    },
    {
      title: vm.translateAttribute("status"),
      property: "status",
      valueFormatter: props => translateEnum("reportRequestStatus", props.value),
    },
  ];

  const actionButtons = (item: InternalReportResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));

    return (
      <>
        {item.status === ReportRequestStatus.Processed && (
          <Button
            variant="success"
            size="sm"
            className="rounded"
            onClick={() => vm.onDownload(item)}
            disabled={isBusy}
            iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
            {vm.translate("downloadFile")}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      {vm.allowWrite && (
        <Form
          className="bg-light mb-4 p-4 rounded"
          onSubmit={preventDefault(vm.onFormSubmit)}
          onReset={preventDefault(vm.onFormReset)}>
          <Row>
            <Form.Group as={Col} sm={6} md={4}>
              <FormLabel required={false}>{vm.translate("fromDate")}</FormLabel>
              <DatePickerBox target={vm.request.reportDateRange} property="from" placeholder={translateGeneral("choose")} />
            </Form.Group>
            <Form.Group as={Col} sm={6} md={4}>
              <FormLabel required={false}>{vm.translate("toDate")}</FormLabel>
              <DatePickerBox target={vm.request.reportDateRange} property="to" placeholder={translateGeneral("choose")} />
            </Form.Group>
            <Form.Group as={Col} sm={12} md={4}>
              <FormLabel required={false}>{vm.translate("gatewayId")}</FormLabel>
              <Input target={vm.request} property="gatewayId" placeholder={translateGeneral("search")} />
            </Form.Group>
          </Row>
          <Row className="d-flex justify-content-end">
            <Form.Group as={Col} sm={6} md="auto" className="mb-0">
              {(isDirty(vm.request) || isDirty(vm.request.reportDateRange)) && (
                <Button iconLeft={IconType.Cross} variant="outline-danger" className="rounded col-sm-12 col-md-auto" type="reset">
                  {translateGeneral("resetFilter")}
                </Button>
              )}
            </Form.Group>
            <Form.Group as={Col} sm={6} md="auto" className="mb-0">
              <Button
                className="d-block rounded col-sm-12 col-md-auto"
                type="submit"
                disabled={isDownloadBusy}
                iconLeft={isDownloadBusy ? IconType.Spinner : undefined}>
                {translateGeneral("generateReport")}
              </Button>
            </Form.Group>
          </Row>
        </Form>
      )}
      <ListView vm={vm} columns={columns} showDetailWithList={true} hideTitle={true} actionButtons={actionButtons} />
    </>
  );
});

registerView(StaticControlReportsView, StaticControlReportsListViewModel);
